import { Badge } from '@/components/ui';
import { cx } from '@/lib/cx';
import { overdueLabel, overdueTitle, type OverdueLabelInput } from '@/lib/overdue-label';

/*
 * THE OVERDUE BRACKET beside a company name on the Accounts dashboard.
 *
 * ```
 * Acme University (GBP 12,400.00 overdue, 45 days)
 * Boston University (GBP 12,400.00 + USD 3,000.00 overdue, 45 days)
 * ```
 *
 * **All of the wording lives in `@/lib/overdue-label` and none of it lives
 * here.** This file is placement, tone and truncation. Read that module's
 * header before changing a word of what appears on screen — it carries the
 * owner's one-bracket-per-currency ruling, the never-sum rule, the decimal-string
 * rule and the pluralisation decision.
 *
 * ## It renders NOTHING when there is nothing overdue
 *
 * `overdueLabel` answers `null` for an empty `totals`, and this returns `null`
 * for that. A company with nothing overdue is the ordinary case on this
 * dashboard, and an empty bracket beside its name would read as a fault in the
 * row rather than as good news.
 *
 * ## Tone, and why the words carry it too
 *
 * `warning`, matching the `Overdue` badge on `/invoices`, whose own comment says
 * it plainly: *a word, not a colour*. **Colour is never the only signal here** —
 * the label contains the word "overdue" and the figure itself, so the row reads
 * correctly in monochrome, to a colour-blind reader, and in a printout. Do not
 * strip the word on the grounds that the tone already says it.
 *
 * ⛔ **NO HELPER TEXT.** The bracket is a value, not an explanation, and nothing
 * may be added beside it that explains what overdue means or what to do about
 * it. The `title` is the documented exception and carries a FACT the truncated
 * chip cannot — see `overdueTitle`.
 *
 * ## It degrades rather than overflows
 *
 * It sits beside a company name in a table cell, and school names here run past
 * 80 characters, so the chip must be allowed to shrink and then truncate rather
 * than widen the column: `max-w-full min-w-0` on the badge, `truncate` on the
 * text, full wording on the `title`. Same idiom as the Item and Tax rate cells
 * in the line grids.
 *
 * ⚠️ **The parent must give it room to shrink into.** A flex or grid item
 * defaults to `min-width: auto`, so a cell without `min-w-0` on it widens to fit
 * this chip instead of letting it truncate — the same failure this file's rules
 * elsewhere record for a wide table inside a grid track. That is the caller's
 * line to write, and it cannot be written from in here.
 */

export interface OverdueChipProps {
  /**
   * What is overdue. `null` / `undefined` renders nothing, so a caller may pass
   * a row's field straight through without guarding first — a company with no
   * overdue balance and one whose summary has not loaded look the same on screen
   * because the honest answer for both is silence.
   */
  summary: OverdueLabelInput | null | undefined;
  className?: string;
}

export function OverdueChip({ summary, className }: OverdueChipProps) {
  if (!summary) return null;

  const label = overdueLabel(summary);
  if (label === null) return null;

  /*
   * `?? label` is unreachable — `overdueTitle` returns null on exactly the
   * condition already handled above — and is written out rather than asserted so
   * a future change to either function cannot silently produce a chip with no
   * tooltip on a truncated figure.
   */
  const title = overdueTitle(summary) ?? label;

  return (
    <Badge
      tone="warning"
      title={title}
      /*
       * No `gap` utility and no second class touching one: `Badge` already sets
       * `gap-1.5`, and two competing utilities for one property do not merge —
       * whichever Tailwind emitted last wins, which is not something a call site
       * gets to decide. One child, so the gap never applies.
       */
      className={cx('max-w-full min-w-0', className)}
    >
      {/*
       * The brackets are part of THIS string rather than sibling elements, for
       * the same reason: siblings would take the badge's own gap and print
       * "( GBP 12,400.00 )". Truncation therefore eats the closing bracket at
       * narrow widths, which the ellipsis makes read as truncated rather than as
       * broken — and the whole wording is on the `title`.
       */}
      <span className="min-w-0 truncate">{`(${label})`}</span>
    </Badge>
  );
}
